import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Artwork,
  ArtistName,
  CollapseButton,
  HiddenEmbed,
  MobileLauncher,
  PlayButton,
  PlayerControls,
  PlayerClearance,
  PlayerDetails,
  PlayerDock,
  PlayerShell,
  PlaylistName,
  ProgressRow,
  ProgressTrack,
  ProgressValue,
  SkipButton,
  Time,
  TrackTitle,
} from "./style";
import playlistTracks, {
  firstPlaylistTrack,
  playlistTrackUris,
} from "./playlist";
import {
  SpotifyEmbedController,
  SpotifyIframeApi,
  SpotifyOEmbedResponse,
  SpotifyPlaybackEvent,
  SpotifyStartedEvent,
} from "./types";

const PLAYLIST_NAME = "fine vibing";
const PLAYLIST_COVER =
  "https://mosaic.scdn.co/300/ab67616d00001e0270e1a429b3fc3f79fedecb4cab67616d00001e02761dc49441e6d2a991d46dc7ab67616d00001e02e81fd3b9a7230db447f38182ab67616d00001e02f66c8c68a9710d44745c7680";
const SPOTIFY_SCRIPT_ID = "spotify-iframe-api";
const SPOTIFY_SCRIPT_URL = "https://open.spotify.com/embed/iframe-api/v1";
const DESKTOP_MEDIA_QUERY = "(min-width: 769px)";
const PROJECT_PATHS = [
  "/traveloka-maudi",
  "/traveloka-sedap",
  "/traveloka-flexible-discovery",
  "/compilation",
  "/contextual-voucher",
  "/blibli-histeria",
  "/blibli-illustration",
  "/blibli-4-you",
  "/blibli-checkout-projects",
  "/final-project",
];

let spotifyApiPromise: Promise<SpotifyIframeApi> | null = null;

const formatTime = (milliseconds: number) => {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return "0:00";

  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const loadSpotifyIframeApi = () => {
  if (spotifyApiPromise) return spotifyApiPromise;

  spotifyApiPromise = new Promise<SpotifyIframeApi>((resolve, reject) => {
    const existingCallback = window.onSpotifyIframeApiReady;

    window.onSpotifyIframeApiReady = (iframeApi) => {
      existingCallback?.(iframeApi);
      resolve(iframeApi);
    };

    const existingScript = document.getElementById(SPOTIFY_SCRIPT_ID);
    if (existingScript) return;

    const script = document.createElement("script");
    script.id = SPOTIFY_SCRIPT_ID;
    script.src = SPOTIFY_SCRIPT_URL;
    script.async = true;
    script.onerror = () => reject(new Error("The Spotify player could not load."));
    document.body.appendChild(script);
  });

  return spotifyApiPromise;
};

const getTrackId = (spotifyUri: string) => {
  const parts = spotifyUri.split(":");
  return parts[0] === "spotify" && parts[1] === "track" ? parts[2] : null;
};

const MusicPlayer = () => {
  const location = useLocation();
  const embedHostRef = useRef<HTMLDivElement>(null);
  const embedWrapperRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<SpotifyEmbedController | null>(null);
  const metadataRequestRef = useRef<AbortController | null>(null);
  const endTimerRef = useRef<number | null>(null);
  const playRetryTimerRef = useRef<number | null>(null);
  const lastPlaybackRef = useRef({ duration: 0, position: 0 });
  const currentTrackIndexRef = useRef(0);
  const [isReady, setReady] = useState(false);
  const [isMobileCollapsed, setMobileCollapsed] = useState(true);
  const [isDocked, setDocked] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isPaused, setPaused] = useState(true);
  const [isBuffering, setBuffering] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [cover, setCover] = useState(firstPlaylistTrack.cover || PLAYLIST_COVER);
  const [trackTitle, setTrackTitle] = useState(firstPlaylistTrack.title);
  const [artistName, setArtistName] = useState(firstPlaylistTrack.artist);

  const playTrackAtIndex = useCallback((requestedIndex: number) => {
    const controller = controllerRef.current;
    if (!controller || playlistTrackUris.length === 0) return;

    if (endTimerRef.current !== null) {
      window.clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }

    if (playRetryTimerRef.current !== null) {
      window.clearTimeout(playRetryTimerRef.current);
      playRetryTimerRef.current = null;
    }

    const trackIndex =
      (requestedIndex + playlistTrackUris.length) % playlistTrackUris.length;
    const trackUri = playlistTrackUris[trackIndex];
    const track = playlistTracks[trackUri];

    currentTrackIndexRef.current = trackIndex;
    setTrackTitle(track.title);
    setArtistName(track.artist);
    setCover(track.cover);
    setPosition(0);
    setDuration(0);
    lastPlaybackRef.current = { duration: 0, position: 0 };
    controller.loadEntity(trackUri, false, 0);
    controller.play();

    // Spotify sometimes needs a moment after loading a new entity before it
    // accepts play. Retrying keeps an ended track moving into the next one.
    playRetryTimerRef.current = window.setTimeout(() => {
      if (currentTrackIndexRef.current === trackIndex) {
        controllerRef.current?.play();
      }
      playRetryTimerRef.current = null;
    }, 450);
  }, []);

  useEffect(() => {
    const isProjectPage = PROJECT_PATHS.some(
      (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
    const isThoughtArticle = location.pathname.startsWith("/thoughts/");
    const isLongFormPage = isProjectPage || isThoughtArticle;
    const desktopQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

    if (!isLongFormPage || !desktopQuery.matches) {
      setDocked(false);
      return;
    }

    let lastScrollY = Math.max(0, window.scrollY);
    let lastScrollTime = performance.now();
    let upwardTravel = 0;
    let frame: number | null = null;

    setDocked(lastScrollY > 420);

    const updatePlayerMode = () => {
      frame = null;
      const currentScrollY = Math.max(0, window.scrollY);
      const currentTime = performance.now();
      const delta = currentScrollY - lastScrollY;
      const elapsed = Math.max(16, currentTime - lastScrollTime);

      if (!desktopQuery.matches || currentScrollY < 180) {
        upwardTravel = 0;
        setDocked(false);
      } else if (delta > 3) {
        upwardTravel = 0;
        if (currentScrollY > 360) setDocked(true);
      } else if (delta < -2) {
        upwardTravel += Math.abs(delta);
        const upwardVelocity = Math.abs(delta) / elapsed;
        const isFastUpwardScroll = upwardVelocity > 0.45 && upwardTravel > 34;

        if (isFastUpwardScroll || upwardTravel > 130) setDocked(false);
      }

      lastScrollY = currentScrollY;
      lastScrollTime = currentTime;
    };

    const handleScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(updatePlayerMode);
    };

    const handleViewportChange = () => {
      if (!desktopQuery.matches) setDocked(false);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    desktopQuery.addListener(handleViewportChange);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      desktopQuery.removeListener(handleViewportChange);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [location.pathname]);

  useEffect(() => {
    let isCancelled = false;

    loadSpotifyIframeApi()
      .then((iframeApi) => {
        if (isCancelled || !embedHostRef.current) return;

        iframeApi.createController(
          embedHostRef.current,
          {
            width: 456,
            height: 352,
            uri: playlistTrackUris[0],
          },
          (controller) => {
            if (isCancelled) {
              controller.destroy();
              return;
            }

            controllerRef.current = controller;

            const spotifyIframe = embedWrapperRef.current?.querySelector("iframe");
            spotifyIframe?.setAttribute(
              "allow",
              "autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            );
            spotifyIframe?.setAttribute("title", "Spotify fine vibing playlist player");

            controller.addListener("ready", () => {
              setReady(true);
            });

            controller.addListener(
              "playback_update",
              (event: SpotifyPlaybackEvent) => {
                const previousPlayback = lastPlaybackRef.current;
                setPaused(event.data.isPaused);
                setBuffering(event.data.isBuffering);
                setPosition(event.data.position);
                setDuration(event.data.duration);

                const pausedAtEnd =
                  event.data.duration > 0 &&
                  event.data.position >= event.data.duration - 900 &&
                  event.data.isPaused;
                const resetAfterEnd =
                  event.data.isPaused &&
                  event.data.position === 0 &&
                  previousPlayback.duration > 0 &&
                  previousPlayback.position >= previousPlayback.duration - 1400;
                const hasFinished = pausedAtEnd || resetAfterEnd;

                lastPlaybackRef.current = {
                  duration: event.data.duration,
                  position: event.data.position,
                };

                if (hasFinished && endTimerRef.current === null) {
                  endTimerRef.current = window.setTimeout(() => {
                    playTrackAtIndex(currentTrackIndexRef.current + 1);
                    endTimerRef.current = null;
                  }, 250);
                }
              }
            );

            controller.addListener(
              "playback_started",
              (event: SpotifyStartedEvent) => {
                if (endTimerRef.current !== null) {
                  window.clearTimeout(endTimerRef.current);
                  endTimerRef.current = null;
                }

                if (playRetryTimerRef.current !== null) {
                  window.clearTimeout(playRetryTimerRef.current);
                  playRetryTimerRef.current = null;
                }

                const savedTrack = playlistTracks[event.data.playingURI];
                if (savedTrack) {
                  const trackIndex = playlistTrackUris.indexOf(
                    event.data.playingURI
                  );
                  if (trackIndex >= 0) {
                    currentTrackIndexRef.current = trackIndex;
                  }
                  setTrackTitle(savedTrack.title);
                  setArtistName(savedTrack.artist);
                  setCover(savedTrack.cover);
                }

                const trackId = getTrackId(event.data.playingURI);
                if (!trackId) return;

                metadataRequestRef.current?.abort();
                const request = new AbortController();
                metadataRequestRef.current = request;
                const trackUrl = `https://open.spotify.com/track/${trackId}`;
                const metadataUrl = `https://open.spotify.com/oembed?url=${encodeURIComponent(
                  trackUrl
                )}`;

                fetch(metadataUrl, { signal: request.signal })
                  .then((response) => {
                    if (!response.ok) throw new Error("Track information unavailable");
                    return response.json() as Promise<SpotifyOEmbedResponse>;
                  })
                  .then((metadata) => {
                    if (!savedTrack && metadata.title) {
                      setTrackTitle(metadata.title);
                      setArtistName("From Freddy's playlist");
                    }
                    if (!savedTrack && metadata.thumbnail_url) {
                      setCover(metadata.thumbnail_url);
                    }
                  })
                  .catch((error) => {
                    if (error instanceof DOMException && error.name === "AbortError") {
                      return;
                    }
                  });
              }
            );
          }
        );
      })
      .catch(() => {
        if (!isCancelled) setHasError(true);
      });

    return () => {
      isCancelled = true;
      metadataRequestRef.current?.abort();
      if (endTimerRef.current !== null) {
        window.clearTimeout(endTimerRef.current);
      }
      if (playRetryTimerRef.current !== null) {
        window.clearTimeout(playRetryTimerRef.current);
      }
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
  }, [playTrackAtIndex]);

  const togglePlayback = () => {
    if (!isReady || !controllerRef.current) return;
    controllerRef.current.togglePlay();
  };

  const playPreviousTrack = () => {
    if (!isReady) return;
    playTrackAtIndex(currentTrackIndexRef.current - 1);
  };

  const playNextTrack = () => {
    if (!isReady) return;
    playTrackAtIndex(currentTrackIndexRef.current + 1);
  };

  const status = hasError
    ? "Player unavailable"
    : isBuffering
    ? "Loading song…"
    : trackTitle;
  const progress = duration > 0 ? Math.min(100, (position / duration) * 100) : 0;
  const controlLabel = !isReady
    ? "Loading playlist"
    : isPaused
    ? `Play ${PLAYLIST_NAME}`
    : `Pause ${trackTitle}`;

  return (
    <>
      <PlayerClearance
        $mobileCollapsed={isMobileCollapsed}
        $docked={isDocked}
        aria-hidden="true"
      />
      <MobileLauncher
        type="button"
        $hidden={!isMobileCollapsed}
        onClick={() => setMobileCollapsed(false)}
        aria-label="Open music player"
        title="Open music player"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 2.3v11.4L13 8 4 2.3Z" fill="currentColor" />
        </svg>
      </MobileLauncher>
      <PlayerDock
        $mobileCollapsed={isMobileCollapsed}
        $docked={isDocked}
        aria-label="Freddy music player"
      >
        <CollapseButton
          type="button"
          onClick={() => setMobileCollapsed(true)}
          aria-label="Collapse music player"
          title="Collapse music player"
        >
          <svg viewBox="0 0 12 12" aria-hidden="true">
            <path
              d="m2.2 4.2 3.8 3.6 3.8-3.6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CollapseButton>
        <PlayerShell $docked={isDocked}>
          <Artwork $docked={isDocked} src={cover} alt="" aria-hidden="true" />
          <PlayerDetails aria-live="polite">
            <PlaylistName>Playlist from Freddy</PlaylistName>
            <TrackTitle>{status}</TrackTitle>
            <ArtistName>{artistName}</ArtistName>
          </PlayerDetails>
          <PlayerControls aria-label="Playback controls">
              <SkipButton
                type="button"
                onClick={playPreviousTrack}
                disabled={!isReady || hasError}
                aria-label="Play previous song"
                title="Play previous song"
              >
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M3 3h1.5v10H3V3Zm1.8 5 7.7-5v10l-7.7-5Z" fill="currentColor" />
                </svg>
              </SkipButton>
              <PlayButton
                type="button"
                onClick={togglePlayback}
                disabled={!isReady || hasError}
                aria-label={controlLabel}
                title={controlLabel}
              >
                {isPaused ? (
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M4 2.3v11.4L13 8 4 2.3Z" fill="currentColor" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M3.5 2.5h3.2v11H3.5v-11Zm5.8 0h3.2v11H9.3v-11Z" fill="currentColor" />
                  </svg>
                )}
              </PlayButton>
              <SkipButton
                type="button"
                onClick={playNextTrack}
                disabled={!isReady || hasError}
                aria-label="Play next song"
                title="Play next song"
              >
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="m3.5 3 7.7 5-7.7 5V3Zm8 0H13v10h-1.5V3Z" fill="currentColor" />
                </svg>
              </SkipButton>
          </PlayerControls>
          <HiddenEmbed ref={embedWrapperRef} aria-hidden="true">
            <div ref={embedHostRef} />
          </HiddenEmbed>
          <ProgressRow $docked={isDocked}>
            <Time>{formatTime(position)}</Time>
            <ProgressTrack
              role="progressbar"
              aria-label="Song progress"
              aria-valuemin={0}
              aria-valuemax={duration}
              aria-valuenow={position}
            >
              <ProgressValue percentage={progress} />
            </ProgressTrack>
            <Time>{formatTime(duration)}</Time>
          </ProgressRow>
        </PlayerShell>
      </PlayerDock>
    </>
  );
};

export default MusicPlayer;
